/** Cadre de l'application : exige une session, affiche la navigation propre à chaque rôle (rail latéral, tiroir sur mobile). */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, Outlet, useLocation } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Archive, BookOpen, ChartColumn, GraduationCap, Newspaper, CalendarClock, CircleHelp, Inbox, Building2, ClipboardCheck, ClipboardSignature, FilePen, FileSpreadsheet, FolderKanban, FolderLock, FolderTree, House, IdCard, LogOut, Mail, Menu, PencilRuler, UsersRound, X, Banknote, Handshake, Eye } from "lucide-react";
import { aideRendezVousVisible, LIEN_RENDEZ_VOUS_AIDE, navigationPour, type CleIcone, type LienNav } from "../navigation";
import { useDeconnexion, useMoi } from "../session";
import { api, type CompteursBoite } from "../api";
import { Chargement, cx } from "../ui/base";
import { Marque, useIdentite } from "../ui/marque";
import { AvatarProfil } from "../ui/Avatar";

const ICONES: Record<CleIcone, (c: string) => React.ReactNode> = {
  formations: (c) => <BookOpen className={c} />,
  outils: (c) => <PencilRuler className={c} />,
  apprenants: (c) => <UsersRound className={c} />,
  convention: (c) => <FilePen className={c} />,
  dossiers: (c) => <FolderKanban className={c} />,
  arborescence: (c) => <FolderTree className={c} />,
  bpf: (c) => <FileSpreadsheet className={c} />,
  courriers: (c) => <Mail className={c} />,
  candidatures: (c) => <ClipboardCheck className={c} />,
  organisme: (c) => <Building2 className={c} />,
  coffre: (c) => <FolderLock className={c} />,
  positionnement: (c) => <ClipboardSignature className={c} />,
  profil: (c) => <IdCard className={c} />,
  archives: (c) => <Archive className={c} />,
  reception: (c) => <Inbox className={c} />,
  aide: (c) => <CircleHelp className={c} />,
  pilotage: (c) => <ChartColumn className={c} />,
  veille: (c) => <Newspaper className={c} />,
  cpf: (c) => <GraduationCap className={c} />,
  financements: (c) => <Banknote className={c} />,
  ambassadeur: (c) => <Handshake className={c} />,
  audience: (c) => <Eye className={c} />,
};

export function Icone({ cle, className = "size-[18px]" }: { cle: CleIcone; className?: string }) {
  return <>{ICONES[cle](className)}</>;
}

const ROLES: Record<string, string> = { admin: "Organisme de formation", formateur: "Formateur", apprenant: "Apprenant" };

export function Cadre() {
  const moi = useMoi();
  const identite = useIdentite();
  const deconnecter = useDeconnexion();
  const { pathname } = useLocation();
  const [tiroir, setTiroir] = useState(false);
  useEffect(() => setTiroir(false), [pathname]);
  // Pastille « Boîte de réception » : messages nouveaux venus du site public (administrateur seulement).
  const estAdmin = moi.data?.acteur?.role === "admin";
  const boite = useQuery({ queryKey: ["boite-reception-compteurs"], queryFn: () => api.get<CompteursBoite>("/admin/boite-reception/compteurs"), enabled: estAdmin, refetchInterval: 120_000 });
  const compteurs: Record<NonNullable<LienNav["compteur"]>, number> = { "boite-reception": boite.data?.total ?? 0 };

  // La garde du routeur (`beforeLoad`) a déjà vérifié la session : ici, l'acteur existe.
  const acteur = moi.data?.acteur;
  if (!acteur) return <div className="px-8"><Chargement /></div>;

  // Menu principal : trois espaces pour le formateur (pédagogique, apprenant, formation) — voir navigation.ts.
  const { accueil, espaces, personnels } = navigationPour(acteur.role, acteur.formateur_valide);
  const lien = (l: LienNav, icone: React.ReactNode) => (
    <Link
      key={l.vers}
      to={l.vers}
      activeOptions={{ exact: l.exact ?? false }}
      className="flex items-center gap-3 rounded-sm px-3 py-2 text-sm font-medium text-encre-2 transition-colors duration-150 hover:bg-papier-3 hover:text-encre"
      activeProps={{ className: "!bg-accent-doux !text-accent-fort" }}
    >
      {icone}
      {l.libelle}
      {l.compteur && compteurs[l.compteur] > 0 && (
        <span className="chiffres ml-auto rounded-full bg-accent px-1.5 text-[11px] font-semibold text-sur-accent" aria-label={`${compteurs[l.compteur]} nouveau(x)`}>
          {compteurs[l.compteur]}
        </span>
      )}
    </Link>
  );
  // Audit du 04/11/2026 (P1-9) : seule la navigation défile ; l'encart d'aide et le pied gardent leur taille (shrink-0)
  // et ne recouvrent jamais « Mode d'emploi » ou « Mon profil ». Un dégradé signale la suite quand elle est cachée.
  const navigation = (
    <NavDefilante>
      {accueil && lien(accueil, <House className="size-[18px]" aria-hidden />)}
      {espaces.map((e) => (
        <div key={e.cle} role="group" aria-label={e.titre} className="mt-3 first:mt-0">
          {espaces.length > 1 && <p className="px-3 pb-1 text-[11px] font-semibold tracking-wide text-encre-3 uppercase">{e.titre}</p>}
          <div className="flex flex-col gap-0.5">{e.liens.map((l) => lien(l, <Icone cle={l.icone} />))}</div>
        </div>
      ))}
      {personnels.length > 0 && (
        <div role="group" aria-label="Mon espace" className="mt-3">
          <p className="px-3 pb-1 text-[11px] font-semibold tracking-wide text-encre-3 uppercase">Mon espace</p>
          <div className="flex flex-col gap-0.5">{personnels.map((l) => lien(l, <Icone cle={l.icone} />))}</div>
        </div>
      )}
    </NavDefilante>
  );
  // Item 3.4 : toujours au même endroit, au-dessus du compte (WCAG 2.2 — 3.2.6 aide cohérente) ; nouvel onglet annoncé.
  const besoinAide = aideRendezVousVisible(acteur.role) && (
    <a href={LIEN_RENDEZ_VOUS_AIDE} target="_blank" rel="noopener noreferrer" className="mt-2 mb-3 flex shrink-0 items-center gap-3 rounded-md border border-attente/70 bg-attente-doux px-3 py-2.5 text-sm font-semibold [@media(max-height:760px)]:mb-2 [@media(max-height:760px)]:py-1.5 text-attente-encre transition-colors duration-150 hover:bg-attente/40">
      <CalendarClock className="size-[18px] shrink-0" aria-hidden />
      <span className="min-w-0 leading-tight">
        Besoin d'aide ?
        <span className="block text-xs font-normal [@media(max-height:760px)]:sr-only">Prendre rendez-vous avec l'équipe</span>
      </span>
      <span className="sr-only"> (s'ouvre dans un nouvel onglet)</span>
    </a>
  );
  const pied = (
    <div className="shrink-0 border-t border-trait pt-3">
      <Link to="/compte" className="flex items-center gap-3 rounded-sm px-3 py-2 hover:bg-papier-3" activeProps={{ className: "!bg-accent-doux" }}>
        <AvatarProfil nom={acteur.nom} photo={moi.data?.photo ?? null} pourcentage={moi.data?.profil_pourcentage ?? null} />
        <span className="min-w-0 leading-tight">
          <span className="block truncate text-sm font-medium">{acteur.nom}</span>
          <span className="block text-xs text-encre-3">{ROLES[acteur.role]}</span>
        </span>
      </Link>
      <button type="button" onClick={deconnecter} className="mt-1 flex w-full items-center gap-3 rounded-sm px-3 py-2 text-sm text-encre-3 hover:bg-papier-3 hover:text-encre">
        <LogOut className="size-[18px]" aria-hidden />
        Se déconnecter
      </button>
    </div>
  );
  // 04/11/2026 — multi-organisme : la marque du menu est celle de l'organisme de l'adresse (logo déposé dans Organisme →
  // Identité ; Skills4mation garde le logo de la vitrine ; sans logo, le nom), lien vers l'accueil.
  const marque = (
    <Link to="/" className="block shrink-0 rounded-sm px-3 pb-6 focus-visible:outline-offset-4" aria-label={`${identite.data?.nom ?? "Accueil"} — accueil`}>
      <Marque taille="h-7" />
    </Link>
  );
  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[248px_minmax(0,1fr)]">
      <aside className="sticky top-0 hidden h-dvh flex-col border-r border-trait bg-papier-2 p-4 lg:flex">
        {marque}
        {navigation}
        {besoinAide}
        {pied}
      </aside>

      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-trait bg-papier/95 px-4 py-3 backdrop-blur lg:hidden">
        <Link to="/" className="flex min-w-0 items-center rounded-sm" aria-label={`${identite.data?.nom ?? "Accueil"} — accueil`}>
          <Marque taille="h-6" className="max-w-[200px]" />
        </Link>
        <span className="flex shrink-0 items-center gap-1">
          {/* Photo de profil (31/10/2026) : on se reconnaît aussi sur téléphone ; elle mène à « Mon compte ». */}
          <Link to="/compte" aria-label={`Mon compte (${acteur.nom})${typeof moi.data?.profil_pourcentage === "number" ? ` — profil complété à ${moi.data.profil_pourcentage} %` : ""}`} className="rounded-full p-1 hover:bg-papier-3">
            <AvatarProfil nom={acteur.nom} photo={moi.data?.photo ?? null} pourcentage={moi.data?.profil_pourcentage ?? null} />
          </Link>
          <button type="button" onClick={() => setTiroir(true)} aria-label="Ouvrir le menu" className="rounded-sm p-2 hover:bg-papier-3">
            <Menu className="size-5" />
          </button>
        </span>
      </header>
      {tiroir && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button type="button" aria-label="Fermer le menu" className="absolute inset-0 bg-encre/40" onClick={() => setTiroir(false)} />
          <div className={cx("absolute inset-y-0 left-0 flex w-72 max-w-[85vw] flex-col bg-papier-2 p-4 shadow-flottant")}>
            <div className="flex items-start justify-between">
              {marque}
              <button type="button" onClick={() => setTiroir(false)} aria-label="Fermer" className="rounded-sm p-1.5 hover:bg-papier-3">
                <X className="size-5" />
              </button>
            </div>
            {navigation}
            {besoinAide}
            {pied}
          </div>
        </div>
      )}

      <main className="min-w-0 px-4 py-6 sm:px-8 sm:py-8">
        <Outlet />
      </main>
    </div>
  );
}

/** Navigation du menu latéral : défile seule (min-h-0), avec un dégradé en bas tant qu'il reste des liens cachés. */
function NavDefilante({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLElement>(null);
  const [suite, setSuite] = useState(false);
  useEffect(() => {
    const nav = ref.current;
    if (!nav) return;
    const maj = () => setSuite(nav.scrollHeight - nav.scrollTop - nav.clientHeight > 4);
    maj();
    nav.addEventListener("scroll", maj, { passive: true });
    const observateur = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(maj);
    observateur?.observe(nav);
    for (const enfant of Array.from(nav.children)) observateur?.observe(enfant);
    return () => {
      nav.removeEventListener("scroll", maj);
      observateur?.disconnect();
    };
  }, []);
  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <nav ref={ref} className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto overscroll-contain" aria-label="Navigation principale">
        {children}
      </nav>
      {suite && <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-papier-2 to-transparent" data-suite-menu />}
    </div>
  );
}
